/**
 * Model-shaped param inputs for one tier slot or one parallel branch.
 *
 * Fields are rendered from `getProviderCapabilities` of *that slot's* provider,
 * so a Gemini rung and an OpenAI rung in the same ladder show different
 * controls. That is the honest presentation: which of these params are legal is
 * a property of the model, which is exactly why they live per slot rather than
 * on the runstep.
 *
 * Placeholders show the provider's own default, so "empty" reads as inherit.
 *
 * See docs/FeatureDesigns/PipelineInfra/provider-and-parameter-management.md §3.
 */

import { getProviderCapabilities, type SlotParams } from '@padcanvas/shared';
import type { ProviderConfig } from '../../types/settings';

const EFFORTS = ['minimal', 'low', 'medium', 'high'] as const;
const SUMMARIES = ['none', 'auto', 'concise', 'detailed'] as const;

export function SlotParamsFields({
  provider,
  params,
  onChange,
  disabled,
}: {
  /** The provider entry this slot points at. Undefined while none is chosen. */
  provider: ProviderConfig | undefined;
  params: SlotParams | undefined;
  onChange: (next: SlotParams | undefined) => void;
  disabled?: boolean;
}) {
  if (!provider) return null;
  const caps = getProviderCapabilities(provider.family, provider.modelName);

  // Nothing model-shaped applies to this model — render nothing rather than an
  // empty box implying there is something to set.
  if (!caps.thinkingBudgetTokens && !caps.reasoningEffort
    && !caps.reasoningSummary && !caps.useStrictJsonSchema) {
    return null;
  }

  /** Patch one key, collapsing an all-empty result back to undefined so the
   *  slot persists without a `params` object at all. */
  function set<K extends keyof SlotParams>(key: K, value: SlotParams[K]) {
    const next: SlotParams = { ...params };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onChange(Object.keys(next).length > 0 ? next : undefined);
  }

  // Decision 11: these two resolve to the same thinking budget by different,
  // per-adapter mappings, so setting both is contradictory. The server rejects
  // it; disabling each when the other is set means nobody has to discover that
  // by hitting a save error.
  const effortSet = params?.reasoningEffort !== undefined;
  const budgetSet = params?.thinkingBudgetTokens !== undefined;

  return (
    <div className="slot-params-fields">
      {caps.thinkingBudgetTokens && (
        <label className="slot-params-field">
          <span>Thinking budget</span>
          <input
            type="number"
            min={0}
            value={params?.thinkingBudgetTokens ?? ''}
            placeholder={provider.thinkingBudgetTokens !== undefined
              ? `inherits: ${provider.thinkingBudgetTokens}` : 'not set'}
            disabled={disabled || effortSet}
            title={effortSet ? 'Clear reasoning effort to set an explicit budget' : undefined}
            onChange={e => set('thinkingBudgetTokens',
              e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
          />
        </label>
      )}
      {caps.reasoningEffort && (
        <label className="slot-params-field">
          <span>Reasoning effort</span>
          <select
            value={params?.reasoningEffort ?? ''}
            disabled={disabled || budgetSet}
            title={budgetSet ? 'Clear the thinking budget to set an effort' : undefined}
            onChange={e => set('reasoningEffort',
              e.target.value === '' ? undefined : e.target.value as SlotParams['reasoningEffort'])}
          >
            <option value="">{provider.reasoningEffort ? `inherits: ${provider.reasoningEffort}` : 'not set'}</option>
            {EFFORTS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
      )}
      {caps.reasoningSummary && (
        <label className="slot-params-field">
          <span>Reasoning summary</span>
          <select
            value={params?.reasoningSummary ?? ''}
            disabled={disabled}
            onChange={e => set('reasoningSummary',
              e.target.value === '' ? undefined : e.target.value as SlotParams['reasoningSummary'])}
          >
            <option value="">{provider.reasoningSummary ? `inherits: ${provider.reasoningSummary}` : 'not set'}</option>
            {SUMMARIES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
      )}
      {caps.useStrictJsonSchema && (
        <label className="slot-params-field slot-params-field-check">
          <input
            type="checkbox"
            checked={params?.useStrictJsonSchema ?? false}
            disabled={disabled}
            onChange={e => set('useStrictJsonSchema', e.target.checked ? true : undefined)}
          />
          <span>Strict JSON schema</span>
        </label>
      )}
    </div>
  );
}
